import { createAsyncThunk } from '@reduxjs/toolkit';
import PaymentHooks from '../utils/PaymentHooks';
import {
  EFTPOS_STATUS_UPDATE,
  FAILURE_REASON,
  PAYMENT_METHOD,
} from '../constants';
import Logger from '../utils/Logger';
import i18next from 'i18next';
import { updateSelectedPaymentMethod } from '../reducers/currentOrder/selectedPaymentMethods';
import { sale } from './sale';
import getSelectedPaymentMethods from '../selectors/getSelectedPaymentMethods';
import getOrderUUID from '../selectors/getOrderUUID';
import { setReceiptLink } from '../reducers/currentOrder/receiptLink';

export const zellerPayments = createAsyncThunk(
  '$zellerPayments',
  async (
    data: {
      amount: number;
    },
    { dispatch, getState, rejectWithValue },
  ) => {
    let reason: FAILURE_REASON | undefined;
    try {
      const { amount } = data;
      const orderId = getOrderUUID(getState() as EntireFrontendState);

      const hook: any = PaymentHooks.get(EFTPOS_STATUS_UPDATE);

      const statusCallback = (statusMsg: string, txnStarted: boolean) => {
        if (hook) {
          hook({ statusMsg: statusMsg, txnStarted: txnStarted });
        }
      };

      statusCallback(i18next.t('zellerNotification.STARTED'), false);
      // @ts-ignore
      const terminal = window.parent._initZeller;

      const promise = terminal.purchase({
        amount: amount,
        reference: orderId || '',
        configuration: {
          unattended: true,
        },
      });
      // Test this is working with fixed zeller package
      PaymentHooks.subscribe('CANCEL_TRANSACTION', data => {
        if (data.cancelled) {
          Logger.log(`===> #### Cancel Transaction Clicked ### ${reason}`);
          promise.cancel();
        }
        promise.cancel();
      });

      //@ts-ignore
      promise.onEvent(event => {
        const enchancedStatus = i18next.t(`zellerNotification.${event.event}`);

        let started = false;
        if (event.event === 'PROCESSING') {
          started = true;
        }
        statusCallback(enchancedStatus, started);
      });

      const response = await promise;
      const isError = [
        'Network Failure',
        'Connection Failure',
        'Setup Required',
        'Feature Not Available',
        'Card Read Failure',
        'Card Insert Required',
        'Operation Failure',
        'Transaction Response Invalid',
        'Transaction Network Failure',
        'Transaction System Failure',
        'Transaction Failure',
      ].includes(response.type);
      const cardCancelled = [
        'Cancelled',
        'Card Blocked',
        'Card Not Supported',
        'Card Declined',
        'Transaction Declined'
      ].includes(response.type);
      const terminalBusy = [
        'Permissions Missing',
        'Operation Busy',
        'Operation Interrupted',
        'Operation Request Invalid',
        'Operation Response Invalid',
        'Paired Device Busy',
        'Inactivity Timeout',
        'Transaction Response Timeout',
        'Paired Device Not Available',
      ].includes(response.type);

      if (terminalBusy) {
        reason = FAILURE_REASON.PAYMENT_TIMED_OUT;
        throw new Error(reason);
      } else if (cardCancelled) {
        reason = FAILURE_REASON.PAYMENT_CANCELLED;
        throw new Error(reason);
      } else if (isError) {
        reason = FAILURE_REASON.PAYMENT_UNSUCCESSFUL;
        throw new Error(reason);
      }

      if (reason) {
        Logger.log(JSON.stringify(`Zeller status ${response.type}`, null, 2));
        statusCallback(response.type, true);
      }

      Logger.log(`-----  ----- ZELLER TRANSACTION  ----- ----`);
      Logger.log(JSON.stringify(response, null, 2));
      Logger.log(`----- ----- ----- -----  -----  -----  -----`);

      const subPayments = getSelectedPaymentMethods(
        getState() as EntireFrontendState,
      );
      if (response.$type === 'Approved' && response.type === 'PURCHASE') {
        let subPayment = subPayments.filter(
          subPayment => subPayment.method === PAYMENT_METHOD.EFTPOS,
        )[0];

        const receiptLink = response?.receiptLink;
        const lineLength = 40;
        let splitReceipt = '';
        for (let i = 0; i < receiptLink.length; i += lineLength) {
          splitReceipt += receiptLink.slice(i, i + lineLength) + '\n';
        }
        subPayment = {
          ...subPayment,
          receiptText: splitReceipt,
          // receiptLink: response?.receiptLink,
          cardType: response?.scheme,
          referenceNumber: response?.transactionUuid,
          // authorisationCode: response?.authCode,
        };
        dispatch(setReceiptLink(response?.receiptLink));
        dispatch(updateSelectedPaymentMethod(subPayment));
        dispatch(sale({ route: 'checkout', authenticationMethod: 'trusted' }));
      }
      if (response.$type === 'Declined' && response.type === 'PURCHASE') {
        reason = FAILURE_REASON.PAYMENT_DECLINED;
        throw new Error(reason);
      }
    } catch (e) {
      Logger.log(`Error ===> Zeller payment failed ${e}`);
      return rejectWithValue({
        e,
        reason: reason ? reason : (e as any).message,
      });
    }
  },
);
